import { useCallback } from 'react';
import { toast } from 'sonner';
import matterService from '@/services/matter/matterService';
import { getTotalRecords, getRows } from '@/utils'; //FetchHelpers

const toMatterOptions = (rows) =>
  rows.map(matter => ({
    label: matter.name,
    value: matter.id.toString(),
    id: matter.id.toString(),
    original: matter,
  }));


const useMatterOptions = ({ defaultPageSize = 20 } = {}) => {
  const loadOptions = useCallback(
    async ({ search = '', page = 1, pageSize = defaultPageSize, contactId } = {}) => {
      try {
        const response = await matterService.getAllMatters({
          page,
          pageSize,
          search,
          contact_id: contactId, // optional filter
        });

        const status =
          typeof response?.status === 'boolean' ? response.status : true;
        if (!status) return { options: [], hasMore: false };

        const rows = getRows(response);
        const options = toMatterOptions(rows);

        const totalRecords = getTotalRecords(response);
        const hasMore =
          typeof totalRecords === 'number'
            ? page * pageSize < totalRecords
            : options.length === pageSize;

        return { options, hasMore };
      } catch (error) {
        console.error('Error', error);
        toast.error('Failed to fetch matters.', {
          description: 'Please try again later.',
        });
        return { options: [], hasMore: false };
      }
    },
    [defaultPageSize]
  );

  return loadOptions;
};

const useMatterLookup = ({ defaultPageSize = 20, maxPages = 50 } = {}) => {
  const fetchAll = useCallback(
    async ({ search = '', contactId } = {}) => {
      const allRows = [];
      const seenIds = new Set();
      let page = 1;
      let hasMore = true;

      while (hasMore && page <= maxPages) {
        try {
          const response = await matterService.getAllMatters({
            page,
            pageSize: defaultPageSize,
            search,
            contact_id: contactId,
          });

          const status =
            typeof response?.status === 'boolean' ? response.status : true;
          if (!status) break;

          const rows = getRows(response);
          if (!rows.length) break;

          let added = 0;
          rows.forEach((row) => {
            const key = row?.id != null ? String(row.id) : null;
            if (key && !seenIds.has(key)) {
              seenIds.add(key);
              allRows.push(row);
              added += 1;
            }
          });

          const totalRecords = getTotalRecords(response);
          hasMore =
            typeof totalRecords === 'number'
              ? page * defaultPageSize < totalRecords
              : false;

          if (added === 0) break;
          page += 1;
        } catch (error) {
          console.error('Error', error);
          toast.error('Failed to fetch matters.', {
            description: 'Please try again later.',
          });
          break;
        }
      }

      return toMatterOptions(allRows);
    },
    [defaultPageSize, maxPages]
  );

  return fetchAll;
};


export { useMatterOptions, useMatterLookup };